import {
  CUSTOM_SLOT_TYPE,
  QUEUE_SORT_BY,
  QUEUE_TYPE,
  RIGHT_PANEL_VIEW_TYPE,
} from "@elixir/types";
import { defaultQueueFilters } from "@elixir/utils";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { QueueStore } from "./types";

const useQueueStore = create<QueueStore>()(
  persist(
    (set, get) => ({
      showAddMedicalHistory: false,
      setShowAddMedicalHistory: (show) => set({ showAddMedicalHistory: show }),

      checkedInSelectedSort: undefined,
      setCheckedInSelectedSort: (sort) => set({ checkedInSelectedSort: sort }),
      completedSelectedSort: undefined,
      setCompletedSelectedSort: (sort) => set({ completedSelectedSort: sort }),
      searchQuery: "",
      setSearchQuery: (query) => set({ searchQuery: query }),
      leftSearchQuery: "",
      setLeftSearchQuery: (query) => set({ leftSearchQuery: query }),
      rescheduleAppointment: undefined,
      setRescheduleAppointment: (appointment) =>
        set({ rescheduleAppointment: appointment }),
      openLeftPanel: true,
      setOpenLeftPanel: (open = true) => set({ openLeftPanel: open }),
      clinicId: "",
      selectedAppointment: undefined,
      setSelectedAppointment: (appointment) =>
        set({ selectedAppointment: appointment }),
      setClinicId: (clinicId) => set({ clinicId }),
      rightPanelViewType: RIGHT_PANEL_VIEW_TYPE.DEFAULT,
      setRightPanelViewType: (rightPanelViewType) =>
        set({ rightPanelViewType }),
      showCustomiseYourQueue: false,
      setShowCustomiseYourQueue: (show) =>
        set({ showCustomiseYourQueue: show }),
      rightPanelQueueType: QUEUE_TYPE.CHECKED_IN,
      leftPanelQueueType: "add-new",
      setRightPanelQueueType: (queueType) =>
        set({ rightPanelQueueType: queueType }),
      setLeftPanelQueueType: (queueType) =>
        set({ leftPanelQueueType: queueType }),
      selectedDate: new Date(),
      customSlotDate: new Date(),
      customSelectedDaySlot: CUSTOM_SLOT_TYPE.TODAY,
      selectedAppointments: [],
      customSlotDuration: 15,
      // timeOfDayForCustomSlot: getTimeOfDaysApplicable(new Date()),
      selectedSlot: undefined,
      setSelectedDate: (date) => set({ selectedDate: date }),
      setCustomSlotDuration: (duration) =>
        set({ customSlotDuration: duration }),
      setCustomSlotDate: (date) => set({ customSlotDate: date }),
      setCustomSelectedDaySlot: (daySlot) =>
        set({ customSelectedDaySlot: daySlot }),
      selectAppointments: (appointments) => {
        set({ selectedAppointments: appointments });
      },
      updateSelectedAppointment: (aptResponse) => {
        const { selectAppointments, selectedAppointments } = get();
        if (selectedAppointments.length === 1) {
          const [selectedApt] = selectedAppointments;

          if (selectedApt) {
            const {
              bookedAppointments = [],
              completedAppointments = [],
              followupAppointments = [],
              opdAppointments = [],
              otherAppointments = [],
            } = aptResponse;

            const updatedApt = [
              ...bookedAppointments,
              ...completedAppointments,
              ...followupAppointments,
              ...opdAppointments,
              ...otherAppointments,
            ].find((apt) => selectedApt.aid === apt.aid);

            if (updatedApt) {
              selectAppointments([updatedApt]);
            }
          }
        }
      },
      removeAppointment: (appointmentId) => {
        const { selectedAppointments } = get();
        const indexOfAptToBeRemoved = selectedAppointments.findIndex(
          (apt) => apt.aid === appointmentId
        );

        set({
          selectedAppointments: [
            ...selectedAppointments.slice(0, indexOfAptToBeRemoved),
            ...selectedAppointments.slice(indexOfAptToBeRemoved + 1),
          ],
        });
      },
      clearSelectedAppointments: () => set({ selectedAppointments: [] }),
      setSelectedSlot: (slot) => set({ selectedSlot: slot }),
      clearSelectedSlot: () => set({ selectedSlot: undefined }),
      clearSlotInfo: () => {
        set({
          customSlotDate: new Date(),
          customSelectedDaySlot: CUSTOM_SLOT_TYPE.TODAY,
          customSlotDuration: 15,
          selectedSlot: undefined,
        });
      },
      setSelectedPatient: (patient) => set({ selectedPatient: patient }),
      clearSelectedPatient: () => set({ selectedPatient: undefined }),
      clearStatusOnAddAptClose: () =>
        set({
          selectedSlot: undefined,
          customSlotDuration: 15,
          customSlotDate: new Date(),
          selectedPatient: undefined,
          selectedAppointments: [],
        }),
      clearStatusOnAptBottomsheetClose: () =>
        set({
          selectedPatient: undefined,
          selectedAppointments: [],
        }),
      leftQueueFilter: defaultQueueFilters,
      setLeftQueueFilter: (filter) => set({ leftQueueFilter: filter }),
      queueFilter: defaultQueueFilters,
      setQueueFilter: (newQueueFilter) => set({ queueFilter: newQueueFilter }),
      clearQueueFilter: () => set({ queueFilter: defaultQueueFilters }),
      setScrollToValues: ({ aid, queueType }) => {
        set({
          scrollToAid: aid,
          scrollToQueue: queueType,
        });
      },
      clearScrollToValues: () => {
        set({ scrollToAid: undefined, scrollToQueue: undefined });
      },
    }),
    {
      name: "fd-local-queue-store",
      partialize: (state) => {
        return {
          openLeftPanel: state.openLeftPanel,
          rightPanelViewType: state.rightPanelViewType,
          completedSelectedSort: state.completedSelectedSort,
          checkedInSelectedSort: state.checkedInSelectedSort,
        };
      },
    }
  )
);

export default useQueueStore;
